import React, {ReactNode} from "react";
import {
	AdminWebserviceStatus,
	Application,
	ApplicationConfigOfficeBridge,
	ApplicationConfigSharePointBridge,
	ApplicationInterface,
	BarcodeWebservice,
	ChromiumBridge,
	ConfigurationType,
	ConverterWebservice,
	Environment,
	ExecutableName,
	ExecutableStatus,
	OcrWebservice,
	PdfaWebservice,
	ServerStatus,
	SignatureWebservice,
	ToolboxWebservice,
	UrlConverterWebservice
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {Accordion, AccordionTab} from "primereact/accordion";
import {InputSwitch} from "primereact/inputswitch";
import WebserviceConverterFields from "../dialog/field/WebserviceConverterFields";
import {InputNumber} from "primereact/inputnumber";
import TimeUnit from "../../shared/units/TimeUnit";
import WebserviceUrlConverterFields from "../dialog/field/WebserviceUrlConverterFields";
import WebserviceSignatureFields from "../dialog/field/WebserviceSignatureFields";
import WebservicePdfaFields from "../dialog/field/WebservicePdfaFields";
import WebserviceOcrFields from "../dialog/field/WebserviceOcrFields";
import WebserviceBarcodeFields from "../dialog/field/WebserviceBarcodeFields";
import WebserviceToolboxFields from "../dialog/field/WebserviceToolboxFields";
import {InputText} from "primereact/inputtext";
import {Password} from "primereact/password";
import {Button} from "primereact/button";
import Administration from "../session/Administration";
import Exception from "../../shared/exception/Exception";
import ErrorCode from "../../shared/exception/ErrorCode";
import MessageBox from "../../shared/components/MessageBox";
import Message from "../../shared/log/Message";
import {MessageType} from "../../shared/log/MessageInterface";
import {AbstractPanel, AbstractPanelProperties, AbstractPanelState} from "./AbstractPanel";
import Immutable from "../../shared/immutable/Immutable";
import Translate from "../../shared/translation/Translate";
import Info, {InfoType} from "../../shared/components/Info";
import Helper from "../../shared/components/Helper";
import FunctionHelper from "../../shared/helper/FunctionHelper";
import DropdownFilter from "../../shared/components/DropdownFilter";
import {SelectItem} from "primereact/selectitem";
import MenuButton from "../../shared/menu/MenuButton";

enum WebserviceStatus {
	OK = "ok",
	DISABLED = "disabled",
	ERROR = "error",
	NOT_LICENSED = "licensed"
}

enum TestCase {
	CONVERTER = "CONVERTER",
	OFFICE = "office",
	SHAREPOINT = "sharepoint",
	CHROMIUM = "chromium",
	TESSERACT = "tesseract",
	PDFTOOLS = "pdftools",
}

export interface WebservicesPanelProperties extends AbstractPanelProperties {
}

export interface WebservicesPanelState extends AbstractPanelState {
	tests: Map<TestCase, boolean>;
	testing: Map<TestCase, boolean>;
	testException: Map<TestCase, Exception>;
	parameter?: Immutable<ApplicationInterface>;
	originalParameter?: Immutable<ApplicationInterface>;
}

export class WebservicesPanelDefinition extends AbstractPanel<WebservicesPanelProperties, WebservicesPanelState> {
	constructor(props: Readonly<WebservicesPanelProperties>) {
		super(props);

		this.state = {
			...super.state,
			tests: new Map<TestCase, boolean>(),
			testing: new Map<TestCase, boolean>([
				[TestCase.CONVERTER, false],
				[TestCase.OFFICE, false],
				[TestCase.SHAREPOINT, false],
				[TestCase.CHROMIUM, false],
				[TestCase.TESSERACT, false],
			]),
			testException: new Map<TestCase, Exception>()
		};

		this.renderTestButton = this.renderTestButton.bind(this);
		this.validate = this.validate.bind(this);
	}

	private updateParameter() {
		this.setState({
			parameter: FunctionHelper.applyValue(this.props.applicationConfig, this.state.parameter),
			originalParameter: this.props.applicationConfig
		});
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();
		this.updateParameter();
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<WebservicesPanelProperties>, prevState: Readonly<WebservicesPanelState>, snapshot?: any
	): Promise<void> {
		if (this.props.contentMode !== prevProps.contentMode ||
			!prevProps.applicationConfig?.equals(this.props.applicationConfig)) {
			this.updateParameter();
		}
	}

	private renderStatus(webserviceStatus: AdminWebserviceStatus | ExecutableStatus | undefined): ReactNode {
		switch (webserviceStatus?.status?.toLowerCase() as WebserviceStatus) {
			case WebserviceStatus.OK:
				return (
					<div className="status">
						<span className="icon icon-play"/>
						<Translate {...this.props} data="adminscreen.panels.webservices.status.ok" namespace="screen"/>
					</div>
				);
			case WebserviceStatus.DISABLED:
				return (
					<div className="status">
						<span className="icon icon-pause"/>
						<Translate {...this.props} data="adminscreen.panels.webservices.status.disabled"
								   namespace="screen"/>
					</div>
				);
			case WebserviceStatus.ERROR:
				return (
					<div className="status">
						<span className="icon icon-warning"/>
						<Translate {...this.props} data="adminscreen.panels.webservices.status.error"
								   namespace="screen"/>
					</div>
				);
			case WebserviceStatus.NOT_LICENSED:
				return (
					<div className="status">
						<span className="icon icon-stop"/>
						<Translate {...this.props} data="adminscreen.panels.webservices.status.not_licensed"
								   namespace="screen"/>
					</div>
				);
		}

		return (
			<div className="status">
				<span className="icon icon-help"/>
				<Translate {...this.props} data="adminscreen.panels.webservices.status.unknown" namespace="screen"/>
			</div>
		);
	}

	protected async onTestError(testCase: TestCase, exception: Error): Promise<boolean> {
		let portalException: Exception = Exception.parseException(exception);

		switch (portalException.code) {
			case ErrorCode.WEBSERVICE_SESSION_TIMEOUT:
				try {
					await this.props.context.session.refresh();
					return true
				} catch (exception: any) {
					await this.onTestError(testCase, exception);
				}
				break;
			default:
				this.state.testException.set(testCase, portalException);
				this.forceUpdate();
				break;
		}

		return false;
	}

	private onChange(
		field: WebserviceConverterFields | WebserviceUrlConverterFields | WebserviceSignatureFields |
			WebservicePdfaFields | WebserviceOcrFields | WebserviceBarcodeFields | WebserviceToolboxFields,
		value: any
	): void {
		let applicationConfig: Application | undefined = Application.fromJson(this.state.parameter?.get());
		let testExceptions: Map<TestCase, Exception> = this.state.testException;

		switch (field) {
			case WebserviceConverterFields.ENABLED:
				applicationConfig!.webservices.converter!.enabled = value;
				testExceptions.delete(TestCase.CONVERTER);
				testExceptions.delete(TestCase.OFFICE);
				testExceptions.delete(TestCase.SHAREPOINT);
				testExceptions.delete(TestCase.CHROMIUM);
				break;
			case WebserviceConverterFields.INSTANCES:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = ConverterWebservice.getInstancesDefault();
				}

				applicationConfig!.webservices.converter!.instances = value;
				break;
			case WebserviceConverterFields.LATENCY:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = ConverterWebservice.getMaxLatencyDefault();
				}

				applicationConfig!.webservices.converter!.maxLatency = value;
				break;
			case WebserviceConverterFields.FONTPATH:
				applicationConfig!.webservices.converter!.fontPath = value;
				break;
			case WebserviceConverterFields.DEFAULT_FONT:
				if (FunctionHelper.isEmpty(value)) {
					applicationConfig!.webservices.converter!.defaultFont = undefined;
					break;
				}

				applicationConfig!.webservices.converter!.defaultFont = value;
				break;
			case WebserviceConverterFields.QUEUE:
				applicationConfig!.webservices.converter!.queueSize = value;
				break;
			case WebserviceConverterFields.OFFICE:
				applicationConfig!.bridges.office!.enabled = value;
				testExceptions.delete(TestCase.OFFICE);
				break;
			case WebserviceConverterFields.CHROMIUM:
				applicationConfig!.bridges.chromium!.enabled = value;
				testExceptions.delete(TestCase.CHROMIUM);
				break;
			case WebserviceConverterFields.CHROMIUM_PORT:
				applicationConfig!.bridges.chromium!.port = value;
				break;
			case WebserviceConverterFields.SHAREPOINT:
				applicationConfig!.bridges.sharePoint!.enabled = value;
				testExceptions.delete(TestCase.SHAREPOINT);
				break;
			case WebserviceConverterFields.SHAREPOINT_URL:
				applicationConfig!.bridges.sharePoint!.url = value;
				break;
			case WebserviceConverterFields.SHAREPOINT_USER:
				applicationConfig!.bridges.sharePoint!.username = value;
				break;
			case WebserviceConverterFields.SHAREPOINT_PASSWORD:
				applicationConfig!.bridges.sharePoint!.password = value;
				break;
			case WebserviceBarcodeFields.ENABLED:
				applicationConfig!.webservices.barcode!.enabled = value;
				break;
			case WebserviceBarcodeFields.INSTANCES:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = BarcodeWebservice.getInstancesDefault();
				}

				applicationConfig!.webservices.barcode!.instances = value;
				break;
			case WebserviceBarcodeFields.LATENCY:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = BarcodeWebservice.getMaxLatencyDefault();
				}

				applicationConfig!.webservices.barcode!.maxLatency = value;
				break;
			case WebserviceBarcodeFields.QUEUE:
				applicationConfig!.webservices.barcode!.queueSize = value;
				break;
			case WebserviceOcrFields.ENABLED:
				applicationConfig!.webservices.ocr!.enabled = value;
				testExceptions.delete(TestCase.TESSERACT);
				break;
			case WebserviceOcrFields.INSTANCES:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = OcrWebservice.getInstancesDefault();
				}

				applicationConfig!.webservices.ocr!.instances = value;
				break;
			case WebserviceOcrFields.LATENCY:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = OcrWebservice.getMaxLatencyDefault();
				}

				applicationConfig!.webservices.ocr!.maxLatency = value;
				break;
			case WebserviceOcrFields.QUEUE:
				applicationConfig!.webservices.ocr!.queueSize = value;
				break;
			case WebservicePdfaFields.ENABLED:
				applicationConfig!.webservices.pdfa!.enabled = value;
				testExceptions.delete(TestCase.PDFTOOLS);
				break;
			case WebservicePdfaFields.INSTANCES:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PdfaWebservice.getInstancesDefault();
				}

				applicationConfig!.webservices.pdfa!.instances = value;
				break;
			case WebservicePdfaFields.LATENCY:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PdfaWebservice.getMaxLatencyDefault();
				}

				applicationConfig!.webservices.pdfa!.maxLatency = value;
				break;
			case WebservicePdfaFields.QUEUE:
				applicationConfig!.webservices.pdfa!.queueSize = value;
				break;
			case WebservicePdfaFields.FONTPATH:
				applicationConfig!.webservices.pdfa!.fontPath = value;
				break;
			case WebserviceSignatureFields.ENABLED:
				applicationConfig!.webservices.signature!.enabled = value;
				break;
			case WebserviceSignatureFields.INSTANCES:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = SignatureWebservice.getInstancesDefault();
				}

				applicationConfig!.webservices.signature!.instances = value;
				break;
			case WebserviceSignatureFields.LATENCY:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = SignatureWebservice.getMaxLatencyDefault();
				}

				applicationConfig!.webservices.signature!.maxLatency = value;
				break;
			case WebserviceSignatureFields.QUEUE:
				applicationConfig!.webservices.signature!.queueSize = value;
				break;
			case WebserviceToolboxFields.ENABLED:
				applicationConfig!.webservices.toolbox!.enabled = value;
				break;
			case WebserviceToolboxFields.INSTANCES:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = ToolboxWebservice.getInstancesDefault();
				}

				applicationConfig!.webservices.toolbox!.instances = value;
				break;
			case WebserviceToolboxFields.LATENCY:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = ToolboxWebservice.getMaxLatencyDefault();
				}

				applicationConfig!.webservices.toolbox!.maxLatency = value;
				break;
			case WebserviceToolboxFields.QUEUE:
				applicationConfig!.webservices.toolbox!.queueSize = value;
				break;
			case WebserviceUrlConverterFields.ENABLED:
				applicationConfig!.webservices.urlconverter!.enabled = value;
				testExceptions.delete(TestCase.CHROMIUM);
				break;
			case WebserviceUrlConverterFields.INSTANCES:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = UrlConverterWebservice.getInstancesDefault();
				}

				applicationConfig!.webservices.urlconverter!.instances = value;
				break;
			case WebserviceUrlConverterFields.LATENCY:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = UrlConverterWebservice.getMaxLatencyDefault();
				}

				applicationConfig!.webservices.urlconverter!.maxLatency = value;
				break;
			case WebserviceUrlConverterFields.QUEUE:
				applicationConfig!.webservices.urlconverter!.queueSize = value;
				break;
			case WebserviceUrlConverterFields.TIMEOUT:
				applicationConfig!.webservices.urlconverter!.timeout = value;
				break;
		}

		this.setState({
			parameter: Immutable.from(applicationConfig.toJson() as ApplicationInterface),
		}, () => this.setState({
			hasChanges: this.hasChanges()
		}));
	}

	private hasChanges(): boolean {
		let parameter: Application | undefined = Application.fromJson(this.state.parameter?.get());
		let originalParameter: Application | undefined = Application.fromJson(this.state.originalParameter?.get());

		if (!FunctionHelper.exists(originalParameter)) {
			return FunctionHelper.exists(parameter);
		}

		return !Immutable.from(parameter.webservices.toJson()).equals(
			Immutable.from(originalParameter?.webservices.toJson())
		) || !Immutable.from(parameter.bridges.toJson()).equals(
			Immutable.from(originalParameter?.bridges.toJson())
		);
	}

	private async validate(testCase: TestCase): Promise<void> {
		let applicationConfig: Application | undefined = Application.fromJson(this.state.parameter?.get());
		if (!FunctionHelper.exists(applicationConfig)) {
			return;
		}

		let tests: Map<TestCase, boolean> = this.state.tests;
		let testing: Map<TestCase, boolean> = this.state.testing;

		testing.set(testCase, true);
		this.forceUpdate();

		let administration: Administration = this.props.context.session.getAdministration();

		try {
			switch (testCase) {
				case TestCase.CONVERTER:
					await administration.testExecutables(
						applicationConfig, [
							ExecutableName.OutsideIn,
							ExecutableName.Fisimple,
							ExecutableName.ImageMagick,
						]);
					break;
				case TestCase.OFFICE:
					await administration.testExecutables(
						applicationConfig, [
							ExecutableName.OfficeBridge
						]);
					break;
				case TestCase.SHAREPOINT:
					await administration.testExecutables(
						applicationConfig, [
							ExecutableName.SharePointBridge
						]);
					break;
				case TestCase.CHROMIUM:
					await administration.testExecutables(
						applicationConfig, [
							ExecutableName.Chromium
						]);
					break;
				case TestCase.TESSERACT:
					await administration.testExecutables(
						applicationConfig, [
							ExecutableName.Tesseract
						]);
					break;
				case TestCase.PDFTOOLS:
					await administration.testExecutables(
						applicationConfig, [
							ExecutableName.Pdftools
						]);
					break;
			}

			tests.set(testCase, true);
			this.state.testException.delete(testCase);
		} catch (exception: any) {
			if (await this.onTestError(testCase, exception)) {
				await this.validate(testCase);
				return;
			}

			tests.set(testCase, false);
		}

		testing.set(testCase, false);

		this.forceUpdate();
	}

	private renderTestException(testCase: TestCase): ReactNode {
		let testException: Exception | undefined = this.state.testException.get(testCase);

		if (!FunctionHelper.exists(testException)) {
			return null;
		}

		let executableCommandline: ReactNode | undefined;
		if (!FunctionHelper.isEmpty(testException?.executableCommandline)) {
			executableCommandline = (
				<>
					<pre>{testException?.executableCommandline}</pre>
				</>
			);
		}

		let executableError: ReactNode | undefined;
		if (!FunctionHelper.isEmpty(testException?.executableError)) {
			executableError = (
				<>
					<hr/>
					<pre>{testException?.executableError}</pre>
				</>
			);
		}

		let stacktrace: ReactNode | undefined;
		if (FunctionHelper.isEmpty(testException?.executableCommandline)) {
			stacktrace = (
				<>
					<hr/>
					<pre>{testException?.stack}</pre>
				</>
			);
		}

		return (
			<div className="p-col-12">
				<MessageBox {...this.props} type={MessageType.DANGER} closeable={true}
							onClose={() => {
								this.state.testException.delete(testCase);
								this.forceUpdate();
							}}
							stacktrace={
								<>
									<p>{testException?.message} (errorcode: {testException?.code},
										exitcode: {testException?.exitCode})</p>
									{executableCommandline}
									{executableError}
									{stacktrace}
								</>
							}>
					{testException?.message}
				</MessageBox>
			</div>
		);
	}

	private renderTestButton(testCase: TestCase, help?: ReactNode): ReactNode {
		let tests: Map<TestCase, boolean> = this.state.tests;
		let testing: Map<TestCase, boolean> = this.state.testing;

		return (
			<Button className="test-button" disabled={testing.get(testCase)}
					icon={"icon" + (
						testing.get(testCase) ? " icon-spinner spin" :
							tests.has(testCase) ? (tests.get(testCase) ? " icon-check" : " icon-close") : " none"
					)} onClick={() => this.validate(testCase)}>
				{Translate.value("buttons.test")}{help}
			</Button>
		);
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let parameter: Application | undefined = Application.fromJson(this.state.parameter?.get());
		let administration: Administration = this.props.context.session.getAdministration();

		let applicationConfiguration: Application = (
			await administration.getConfiguration(ConfigurationType.Application) as Application
		).clone();
		applicationConfiguration.webservices = parameter!.webservices;
		applicationConfiguration.bridges = parameter!.bridges;

		try {
			await administration.updateConfiguration(ConfigurationType.Application, applicationConfiguration);
			await administration.updateServerStatus();

			this.setState({
				submitted: false,
				hasChanges: false,
				parameter: this.state.originalParameter
			});
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.submit();
			}
			return;
		}

		let message: Message = new Message("success.administration.webservices", MessageType.SUCCESS);
		this.props.context.getPage().addMessage(message);
		this.props.context.getPage().setState({
			showRestartMessage: true
		});
	}

	private async reset(): Promise<void> {
		this.state.testException.clear();
		this.state.tests.clear();

		this.setState({
			exception: undefined,
			hasChanges: false,
			parameter: this.state.originalParameter
		});
	}

	private renderConverterTab(applicationConfig: Application, serverStatus: ServerStatus): ReactNode {
		let converterEnabled: boolean = FunctionHelper.applyValue(
			applicationConfig.webservices.converter?.enabled, ConverterWebservice.getEnabledDefault()
		);
		let officeBridgeEnabled: boolean = FunctionHelper.applyValue(
			applicationConfig.bridges.office?.enabled, ApplicationConfigOfficeBridge.getEnabledDefault()
		);
		let sharepointEnabled: boolean = FunctionHelper.applyValue(
			applicationConfig.bridges.sharePoint?.enabled, ApplicationConfigSharePointBridge.getEnabledDefault()
		);
		let chromiumEnabled: boolean = FunctionHelper.applyValue(
			applicationConfig.bridges.chromium?.enabled, ChromiumBridge.getEnabledDefault()
		);

		let converterStatus: WebserviceStatus = FunctionHelper.applyValue(
			serverStatus.webservices?.converter?.status, AdminWebserviceStatus.getStatusDefault()
		).toLowerCase() as WebserviceStatus;
		let officeStatus: WebserviceStatus = FunctionHelper.applyValue(
			serverStatus.webservices?.converter?.executables?.office?.status, ExecutableStatus.getStatusDefault()
		).toLowerCase() as WebserviceStatus;
		let sharepointStatus: WebserviceStatus = FunctionHelper.applyValue(
			serverStatus.webservices?.converter?.executables?.sharepoint?.status, ExecutableStatus.getStatusDefault()
		).toLowerCase() as WebserviceStatus;
		let chromiumStatus: WebserviceStatus = FunctionHelper.applyValue(
			serverStatus.webservices?.converter?.executables?.chromium?.status, ExecutableStatus.getStatusDefault()
		).toLowerCase() as WebserviceStatus;

		let fonts: Array<SelectItem> = [{
			label: Translate.value("adminscreen.panels.webservices.font.none", {ns: "screen"}),
			value: ""
		}];
		for (let font of FunctionHelper.applyValue(
			this.props.context.info.environment?.fonts, Environment.getFontsDefault()
		)) {
			fonts.push({
				label: font,
				value: font
			});
		}

		return (
			<AccordionTab key={"converter"} className={converterStatus === WebserviceStatus.ERROR ? "errorneous" : ""}
						  headerTemplate={
							  <>
								  <Translate {...this.props} data="adminscreen.panels.webservices.converter"
											 namespace="screen"/>
								  {this.renderStatus(serverStatus.webservices?.converter)}
							  </>
						  }>
				<fieldset className="p-col-12 p-grid">
					{this.renderTestException(TestCase.CONVERTER)}
					<div className="p-col-12 p-lg-6 p-grid">
						<label className="p-col-12 p-sm-5" htmlFor="control-converter-enabled">
							<Translate {...this.props} data="adminscreen.panels.webservices.enabled"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={ConverterWebservice.getEnabledDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-converter-active" checked={converterEnabled}
										 onChange={(e) => this.onChange(
											 WebserviceConverterFields.ENABLED, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={!converterEnabled}>
						<label className="p-col-12 p-sm-5"
							   htmlFor="control-converter-instances">
							<Translate {...this.props} data="adminscreen.panels.webservices.instances"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={ConverterWebservice.getInstancesDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-converter-instances" step={1} showButtons={true}
										 useGrouping={false}
										 value={FunctionHelper.applyValue(
											 applicationConfig.webservices.converter?.instances,
											 ConverterWebservice.getInstancesDefault()
										 )}
										 min={ConverterWebservice.getInstancesMin()}
										 onChange={(e) => this.onChange(
											 WebserviceConverterFields.INSTANCES, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={!converterEnabled}>
						<label className="p-col-12 p-sm-5" htmlFor="control-converter-queue">
							<Translate {...this.props} data="adminscreen.panels.webservices.queue_size"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={ConverterWebservice.getQueueSizeDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-converter-queue" min={0} step={1}
										 value={FunctionHelper.applyValue(
											 applicationConfig.webservices.converter?.queueSize,
											 ConverterWebservice.getQueueSizeDefault()
										 )}
										 showButtons={true}
										 onChange={(e) => this.onChange(
											 WebserviceConverterFields.QUEUE, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={!converterEnabled}>
						<label className="p-col-12 p-sm-5"
							   htmlFor="control-converter-maxlatency">
							<Translate {...this.props} data="adminscreen.panels.webservices.maxlatency"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={ConverterWebservice.getMaxLatencyDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber step={1} inputId="control-converter-maxlatency" useGrouping={false}
										 value={FunctionHelper.applyValue(
											 applicationConfig.webservices.converter?.maxLatency,
											 ConverterWebservice.getMaxLatencyDefault()
										 )}
										 min={ConverterWebservice.getMaxLatencyMin()}
										 showButtons={true} suffix={" " + TimeUnit.SECOND}
										 onChange={(e) => this.onChange(
											 WebserviceConverterFields.LATENCY, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={!converterEnabled}>
						<label className="p-col-12 p-sm-5" htmlFor="control-converter-fontpath">
							<Translate {...this.props} data="adminscreen.panels.webservices.font.path"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={ConverterWebservice.getFontPathDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputText id="control-converter-fontpath"
									   value={FunctionHelper.applyValue(
										   applicationConfig.webservices.converter?.fontPath,
										   ConverterWebservice.getFontPathDefault()
									   )}
									   onInput={(e) => this.onChange(
										   WebserviceConverterFields.FONTPATH, e.currentTarget.value
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={!converterEnabled}>
						<label className="p-col-12 p-sm-5" htmlFor="control-converter-default-font">
							<Translate {...this.props} data="adminscreen.panels.webservices.font.default"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={ConverterWebservice.getDefaultFontDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<DropdownFilter id="control-converter-default-font" options={fonts}
											onClear={() => this.onChange(
												WebserviceConverterFields.DEFAULT_FONT, ""
											)}
											value={FunctionHelper.applyValue(
												applicationConfig.webservices.converter?.defaultFont,
												ConverterWebservice.getDefaultFontDefault()
											)}
											appendTo={document.getElementById("root")}
											onChange={(e) => this.onChange(
												WebserviceConverterFields.DEFAULT_FONT, e.value
											)}/>
						</div>
					</div>
					<div className="p-col-12 p-grid" hidden={!converterEnabled}>
						<div className="p-col-12 p-grid" style={{
							justifyContent: "flex-end"
						}}>
							{this.renderTestButton(TestCase.CONVERTER)}
						</div>
					</div>
				</fieldset>
				<fieldset className={"p-col-12 p-grid" + (officeStatus === WebserviceStatus.ERROR ? " errorneous" : "")}
						  hidden={!converterEnabled}>
					<legend>
						<Translate {...this.props} data="adminscreen.panels.webservices.office.label"
								   namespace="screen"/>
						{this.renderStatus(serverStatus.webservices?.converter.executables?.office)}
					</legend>
					{this.renderTestException(TestCase.OFFICE)}
					<div className="p-col-12 p-lg-6 p-grid">
						<label className="p-col-12 p-sm-5" htmlFor="control-converter-office-enabled">
							<Translate {...this.props} data="adminscreen.panels.webservices.office.enabled"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={ApplicationConfigOfficeBridge.getEnabledDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-converter-office-enabled" checked={officeBridgeEnabled}
										 onChange={(e) => this.onChange(
											 WebserviceConverterFields.OFFICE, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-grid" hidden={!officeBridgeEnabled}>
						<div className="p-col-12 p-grid" style={{
							justifyContent: "flex-end"
						}}>
							{this.renderTestButton(TestCase.OFFICE)}
						</div>
					</div>
				</fieldset>
				<fieldset className={
					"p-col-12 p-grid" + (sharepointStatus === WebserviceStatus.ERROR ? " errorneous" : "")
				} hidden={!converterEnabled}>
					<legend>
						<Translate {...this.props} data="adminscreen.panels.webservices.sharepoint.label"
								   namespace="screen"/>
						{this.renderStatus(serverStatus.webservices?.converter.executables?.sharepoint)}
					</legend>
					{this.renderTestException(TestCase.SHAREPOINT)}
					<div className="p-col-12 p-lg-6 p-grid">
						<label className="p-col-12 p-sm-5" htmlFor="control-converter-sharepoint-enabled">
							<Translate {...this.props} data="adminscreen.panels.webservices.sharepoint.enabled"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={ApplicationConfigSharePointBridge.getEnabledDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-converter-sharepoint-enabled" checked={sharepointEnabled}
										 onChange={(e) => this.onChange(
											 WebserviceConverterFields.SHAREPOINT, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={!sharepointEnabled}>
						<label className="p-col-12 p-sm-5" htmlFor="control-converter-sharepoint-url">
							<Translate {...this.props} data="adminscreen.panels.webservices.sharepoint.url"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={ApplicationConfigSharePointBridge.getUrlDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputText id="control-converter-sharepoint-url"
									   value={FunctionHelper.applyValue(
										   applicationConfig.bridges.sharePoint?.url,
										   ApplicationConfigSharePointBridge.getUrlDefault()
									   )}
									   onInput={(e) => this.onChange(
										   WebserviceConverterFields.SHAREPOINT_URL, e.currentTarget.value
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={!sharepointEnabled}>
						<label className="p-col-12 p-sm-5" htmlFor="control-converter-sharepoint-user">
							<Translate {...this.props} data="adminscreen.panels.webservices.sharepoint.user"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={ApplicationConfigSharePointBridge.getUsernameDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputText id="control-converter-sharepoint-user"
									   value={FunctionHelper.applyValue(
										   applicationConfig.bridges.sharePoint?.username,
										   ApplicationConfigSharePointBridge.getUsernameDefault()
									   )}
									   onInput={(e) => this.onChange(
										   WebserviceConverterFields.SHAREPOINT_USER, e.currentTarget.value
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={!sharepointEnabled}>
						<label className="p-col-12 p-sm-5"
							   htmlFor="control-converter-sharepoint-password">
							<Translate {...this.props} data="adminscreen.panels.webservices.sharepoint.password"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={ApplicationConfigSharePointBridge.getPasswordDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<Password inputId="control-converter-sharepoint-password"
									  feedback={false} toggleMask={true}
									  value={FunctionHelper.applyValue(
										  applicationConfig.bridges.sharePoint?.password,
										  ApplicationConfigSharePointBridge.getPasswordDefault()
									  )}
									  onChange={(e) => this.onChange(
										  WebserviceConverterFields.SHAREPOINT_PASSWORD, e.currentTarget.value
									  )}/>
						</div>
					</div>
				</fieldset>
				<fieldset className={
					"p-col-12 p-grid" + (chromiumStatus === WebserviceStatus.ERROR ? " errorneous" : "")
				} hidden={!converterEnabled}>
					<legend>
						<Translate {...this.props} data="adminscreen.panels.webservices.chromium.label"
								   namespace="screen"/>
						{this.renderStatus(serverStatus.webservices?.converter.executables?.chromium)}
					</legend>
					{this.renderTestException(TestCase.CHROMIUM)}
					<div className="p-col-12 p-lg-6 p-grid">
						<label className="p-col-12 p-sm-5" htmlFor="control-converter-chromium-enabled">
							<Translate {...this.props} data="adminscreen.panels.webservices.chromium.enabled"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={ChromiumBridge.getEnabledDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-converter-chromium-enabled" checked={chromiumEnabled}
										 onChange={(e) => this.onChange(
											 WebserviceConverterFields.CHROMIUM, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={!chromiumEnabled}>
						<label className="p-col-12 p-sm-5"
							   htmlFor="control-converter-chromium-port">
							<Translate {...this.props} data="adminscreen.panels.webservices.chromium.port.label"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={ChromiumBridge.getPortDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-converter-chromium-port" min={0} step={1} showButtons={true}
										 useGrouping={false}
										 value={FunctionHelper.applyValue(
											 applicationConfig.bridges.chromium?.port, ChromiumBridge.getPortDefault()
										 )}
										 onChange={(e) => this.onChange(
											 WebserviceConverterFields.CHROMIUM_PORT, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-grid" hidden={!chromiumEnabled}>
						<div className="p-col-12 p-grid" style={{
							justifyContent: "flex-end"
						}}>
							{this.renderTestButton(TestCase.CHROMIUM)}
						</div>
					</div>
				</fieldset>
			</AccordionTab>
		);
	}

	private renderUrlConverterTab(applicationConfig: Application, serverStatus: ServerStatus): ReactNode {
		let urlconverterEnabled: boolean = FunctionHelper.applyValue(
			applicationConfig.webservices.urlconverter?.enabled, UrlConverterWebservice.getEnabledDefault()
		);
		let urlConverterStatus: WebserviceStatus = FunctionHelper.applyValue(
			serverStatus.webservices?.urlconverter?.status, ExecutableStatus.getStatusDefault()
		).toLowerCase() as WebserviceStatus;

		return (
			<AccordionTab key={"urlconverter"}
						  className={urlConverterStatus === WebserviceStatus.ERROR ? "errorneous" : ""}
						  headerTemplate={
							  <>
								  <Translate {...this.props} data="adminscreen.panels.webservices.urlconverter"
											 namespace="screen"/>
								  {this.renderStatus(serverStatus.webservices?.urlconverter)}
							  </>
						  }>
				<fieldset className="p-col-12 p-grid">
					{urlConverterStatus === WebserviceStatus.ERROR &&
                        <MessageBox {...this.props} type={MessageType.WARNING} className="p-col-12">
                            <Translate {...this.props} data="warnings.admin.webservices.chromium"
                                       namespace="message"/>
                        </MessageBox>
					}
					<div className="p-col-12 p-lg-6 p-grid">
						<label className="p-col-12 p-sm-5" htmlFor="control-urlconverter-enabled">
							<Translate {...this.props} data="adminscreen.panels.webservices.enabled"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={UrlConverterWebservice.getEnabledDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch checked={urlconverterEnabled} inputId="control-urlconverter-active"
										 onChange={(e) => this.onChange(
											 WebserviceUrlConverterFields.ENABLED, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={!urlconverterEnabled}>
						<label className="p-col-12 p-sm-5" htmlFor="control-urlconverter-instances">
							<Translate {...this.props} data="adminscreen.panels.webservices.instances"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={UrlConverterWebservice.getInstancesDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-urlconverter-instances" step={1} showButtons={true}
										 useGrouping={false}
										 value={FunctionHelper.applyValue(
											 applicationConfig.webservices.urlconverter?.instances,
											 UrlConverterWebservice.getInstancesDefault()
										 )}
										 min={UrlConverterWebservice.getInstancesMin()}
										 onChange={(e) => this.onChange(
											 WebserviceUrlConverterFields.INSTANCES, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={!urlconverterEnabled}>
						<label className="p-col-12 p-sm-5" htmlFor="control-urlconverter-queue">
							<Translate {...this.props} data="adminscreen.panels.webservices.queue_size"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={UrlConverterWebservice.getQueueSizeDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-urlconverter-queue" min={0} step={1} showButtons={true}
										 useGrouping={false}
										 value={FunctionHelper.applyValue(
											 applicationConfig.webservices.urlconverter?.queueSize,
											 UrlConverterWebservice.getQueueSizeDefault()
										 )}
										 onChange={(e) => this.onChange(
											 WebserviceUrlConverterFields.QUEUE, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={!urlconverterEnabled}>
						<label className="p-col-12 p-sm-5" htmlFor="control-urlconverter-maxlatency">
							<Translate {...this.props} data="adminscreen.panels.webservices.maxlatency"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={UrlConverterWebservice.getMaxLatencyDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-urlconverter-maxlatency" step={1} showButtons={true}
										 useGrouping={false}
										 value={FunctionHelper.applyValue(
											 applicationConfig.webservices.urlconverter?.maxLatency,
											 UrlConverterWebservice.getMaxLatencyDefault()
										 )}
										 min={UrlConverterWebservice.getMaxLatencyMin()} suffix={" " + TimeUnit.SECOND}
										 onChange={(e) => this.onChange(
											 WebserviceUrlConverterFields.LATENCY, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={!urlconverterEnabled}>
						<label className="p-col-12 p-sm-5" htmlFor="control-urlconverter-timeout">
							<Translate {...this.props} data="adminscreen.panels.webservices.timeout"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={UrlConverterWebservice.getTimeoutDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-urlconverter-timeout" min={0} step={1} showButtons={true}
										 suffix={" " + TimeUnit.MILLISECOND} useGrouping={false}
										 value={FunctionHelper.applyValue(
											 applicationConfig.webservices.urlconverter?.timeout,
											 UrlConverterWebservice.getTimeoutDefault()
										 )}
										 onChange={(e) => this.onChange(
											 WebserviceUrlConverterFields.TIMEOUT, e.value
										 )
										 }/>
						</div>
					</div>
				</fieldset>
			</AccordionTab>
		)
	}

	private renderSignatureTab(applicationConfig: Application, serverStatus: ServerStatus): ReactNode {
		let signatureEnabled: boolean = FunctionHelper.applyValue(
			applicationConfig.webservices.signature?.enabled, SignatureWebservice.getEnabledDefault()
		);
		let signatureStatus: WebserviceStatus = FunctionHelper.applyValue(
			serverStatus.webservices?.signature?.status, ExecutableStatus.getStatusDefault()
		).toLowerCase() as WebserviceStatus;

		return (
			<AccordionTab key={"signature"} className={signatureStatus === WebserviceStatus.ERROR ? "errorneous" : ""}
						  headerTemplate={
							  <>
								  <Translate {...this.props} data="adminscreen.panels.webservices.signature"
											 namespace="screen"/>
								  {this.renderStatus(serverStatus.webservices?.signature)}
							  </>
						  }>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid">
						<label className="p-col-12 p-sm-5" htmlFor="control-signature-enabled">
							<Translate {...this.props} data="adminscreen.panels.webservices.enabled"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={SignatureWebservice.getEnabledDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-signature-active" checked={signatureEnabled}
										 onChange={(e) => this.onChange(
											 WebserviceSignatureFields.ENABLED, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={!signatureEnabled}>
						<label className="p-col-12 p-sm-5" htmlFor="control-signature-instances">
							<Translate {...this.props} data="adminscreen.panels.webservices.instances"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={SignatureWebservice.getInstancesDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-signature-instances" step={1} showButtons={true}
										 useGrouping={false}
										 value={FunctionHelper.applyValue(
											 applicationConfig.webservices.signature?.instances,
											 SignatureWebservice.getInstancesDefault()
										 )}
										 min={SignatureWebservice.getInstancesMin()}
										 onChange={(e) => this.onChange(
											 WebserviceSignatureFields.INSTANCES, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={!signatureEnabled}>
						<label className="p-col-12 p-sm-5" htmlFor="control-signature-queue">
							<Translate {...this.props} data="adminscreen.panels.webservices.queue_size"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={SignatureWebservice.getQueueSizeDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-signature-queue" min={0} step={1} showButtons={true}
										 useGrouping={false}
										 value={FunctionHelper.applyValue(
											 applicationConfig.webservices.signature?.queueSize,
											 SignatureWebservice.getQueueSizeDefault()
										 )}
										 onChange={(e) => this.onChange(
											 WebserviceSignatureFields.QUEUE, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={!signatureEnabled}>
						<label className="p-col-12 p-sm-5" htmlFor="control-signature-maxlatency">
							<Translate {...this.props} data="adminscreen.panels.webservices.maxlatency"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={SignatureWebservice.getMaxLatencyDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-signature-maxlatency" step={1} showButtons={true}
										 suffix={" " + TimeUnit.SECOND} useGrouping={false}
										 value={FunctionHelper.applyValue(
											 applicationConfig.webservices.signature?.maxLatency,
											 SignatureWebservice.getMaxLatencyDefault()
										 )}
										 min={SignatureWebservice.getMaxLatencyMin()}
										 onChange={(e) => this.onChange(
											 WebserviceSignatureFields.LATENCY, e.value
										 )}/>
						</div>
					</div>
				</fieldset>
			</AccordionTab>
		)
	}

	private renderPdfaTab(applicationConfig: Application, serverStatus: ServerStatus): ReactNode {
		let pdfaEnabled: boolean = FunctionHelper.applyValue(
			applicationConfig.webservices.pdfa?.enabled, PdfaWebservice.getEnabledDefault()
		);
		let pdfaStatus: WebserviceStatus = FunctionHelper.applyValue(
			serverStatus.webservices?.pdfa?.status, ExecutableStatus.getStatusDefault()
		).toLowerCase() as WebserviceStatus;

		return (
			<AccordionTab key={"pdfa"} className={pdfaStatus === WebserviceStatus.ERROR ? "errorneous" : ""}
						  headerTemplate={
							  <>
								  <Translate {...this.props} data="adminscreen.panels.webservices.pdfa"
											 namespace="screen"/>
								  {this.renderStatus(serverStatus.webservices?.pdfa)}
							  </>
						  }>
				<fieldset className="p-col-12 p-grid">
					{this.renderTestException(TestCase.PDFTOOLS)}
					<div className="p-col-12 p-lg-6 p-grid">
						<label className="p-col-12 p-sm-5" htmlFor="control-pdfa-enabled">
							<Translate {...this.props} data="adminscreen.panels.webservices.enabled"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={PdfaWebservice.getEnabledDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-pdfa-active" checked={pdfaEnabled}
										 onChange={(e) => this.onChange(
											 WebservicePdfaFields.ENABLED, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={!pdfaEnabled}>
						<label className="p-col-12 p-sm-5" htmlFor="control-pdfa-instances">
							<Translate {...this.props} data="adminscreen.panels.webservices.instances"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={PdfaWebservice.getInstancesDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-pdfa-instances" step={1} showButtons={true}
										 useGrouping={false}
										 value={FunctionHelper.applyValue(
											 applicationConfig.webservices.pdfa?.instances,
											 PdfaWebservice.getInstancesDefault()
										 )}
										 min={PdfaWebservice.getInstancesMin()}
										 onChange={(e) => this.onChange(
											 WebservicePdfaFields.INSTANCES, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={!pdfaEnabled}>
						<label className="p-col-12 p-sm-5" htmlFor="control-pdfa-queue">
							<Translate {...this.props} data="adminscreen.panels.webservices.queue_size"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={PdfaWebservice.getQueueSizeDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-pdfa-queue" min={0} step={1} showButtons={true}
										 useGrouping={false}
										 value={FunctionHelper.applyValue(
											 applicationConfig.webservices.pdfa?.queueSize,
											 PdfaWebservice.getQueueSizeDefault()
										 )}
										 onChange={(e) => this.onChange(
											 WebservicePdfaFields.QUEUE, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={!pdfaEnabled}>
						<label className="p-col-12 p-sm-5" htmlFor="control-pdfa-maxlatency">
							<Translate {...this.props} data="adminscreen.panels.webservices.maxlatency"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={PdfaWebservice.getMaxLatencyDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-pdfa-maxlatency" step={1} showButtons={true}
										 suffix={" " + TimeUnit.SECOND} useGrouping={false}
										 value={FunctionHelper.applyValue(
											 applicationConfig.webservices.pdfa?.maxLatency,
											 PdfaWebservice.getMaxLatencyDefault()
										 )}
										 min={PdfaWebservice.getMaxLatencyMin()}
										 onChange={(e) => this.onChange(
											 WebservicePdfaFields.LATENCY, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={!pdfaEnabled}>
						<label className="p-col-12 p-sm-5" htmlFor="control-pdfa-fontpath">
							<Translate {...this.props} data="adminscreen.panels.webservices.fontpath"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={PdfaWebservice.getFontPathDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputText id="control-pdfa-fontpath"
									   value={FunctionHelper.applyValue(
										   applicationConfig.webservices.pdfa?.fontPath,
										   PdfaWebservice.getFontPathDefault()
									   )}
									   onInput={(e) => this.onChange(
										   WebservicePdfaFields.FONTPATH, e.currentTarget.value
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-grid" hidden={!pdfaEnabled}>
						<div className="p-col-12 p-grid" style={{
							justifyContent: "flex-end"
						}}>
							{this.renderTestButton(TestCase.PDFTOOLS)}
						</div>
					</div>
				</fieldset>
			</AccordionTab>
		);
	}

	private renderOcrTab(applicationConfig: Application, serverStatus: ServerStatus): ReactNode {
		let ocrEnabled: boolean = FunctionHelper.applyValue(
			applicationConfig.webservices.ocr?.enabled, OcrWebservice.getEnabledDefault()
		);
		let ocrStatus: WebserviceStatus = FunctionHelper.applyValue(
			serverStatus.webservices?.ocr?.status, ExecutableStatus.getStatusDefault()
		).toLowerCase() as WebserviceStatus;

		return (
			<AccordionTab key={"ocr"} className={ocrStatus === WebserviceStatus.ERROR ? "errorneous" : ""}
						  headerTemplate={
							  <>
								  <Translate {...this.props} data="adminscreen.panels.webservices.ocr"
											 namespace="screen"/>
								  {this.renderStatus(serverStatus.webservices?.ocr)}
							  </>
						  }>
				<fieldset className="p-col-12 p-grid">
					{this.renderTestException(TestCase.TESSERACT)}
					<div className="p-col-12 p-lg-6 p-grid">
						<label className="p-col-12 p-sm-5" htmlFor="control-ocr-enabled">
							<Translate {...this.props} data="adminscreen.panels.webservices.enabled"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={OcrWebservice.getEnabledDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-ocr-active" checked={ocrEnabled}
										 onChange={(e) => this.onChange(
											 WebserviceOcrFields.ENABLED, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={!ocrEnabled}>
						<label className="p-col-12 p-sm-5" htmlFor="control-ocr-instances">
							<Translate {...this.props} data="adminscreen.panels.webservices.instances"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={OcrWebservice.getInstancesDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-ocr-instances" step={1} showButtons={true} useGrouping={false}
										 value={FunctionHelper.applyValue(
											 applicationConfig.webservices.ocr?.instances,
											 OcrWebservice.getInstancesDefault()
										 )}
										 min={OcrWebservice.getInstancesMin()}
										 onChange={(e) => this.onChange(
											 WebserviceOcrFields.INSTANCES, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={!ocrEnabled}>
						<label className="p-col-12 p-sm-5" htmlFor="control-ocr-queue">
							<Translate {...this.props} data="adminscreen.panels.webservices.queue_size"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={OcrWebservice.getQueueSizeDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-ocr-queue" min={0} step={1} showButtons={true}
										 useGrouping={false}
										 value={FunctionHelper.applyValue(
											 applicationConfig.webservices.ocr?.queueSize,
											 OcrWebservice.getQueueSizeDefault()
										 )}
										 onChange={(e) => this.onChange(
											 WebserviceOcrFields.QUEUE, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={!ocrEnabled}>
						<label className="p-col-12 p-sm-5" htmlFor="control-ocr-maxlatency">
							<Translate {...this.props} data="adminscreen.panels.webservices.maxlatency"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={OcrWebservice.getMaxLatencyDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-ocr-maxlatency" showButtons={true} step={1}
										 suffix={" " + TimeUnit.SECOND} useGrouping={false}
										 value={FunctionHelper.applyValue(
											 applicationConfig.webservices.ocr?.maxLatency,
											 OcrWebservice.getMaxLatencyDefault()
										 )}
										 min={OcrWebservice.getMaxLatencyMin()}
										 onChange={(e) => this.onChange(
											 WebserviceOcrFields.LATENCY, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-grid" hidden={!ocrEnabled}>
						<div className="p-col-12 p-grid" style={{
							justifyContent: "flex-end"
						}}>
							{this.renderTestButton(TestCase.TESSERACT)}
						</div>
					</div>
				</fieldset>
			</AccordionTab>
		);
	}

	private renderBarcodeTab(applicationConfig: Application, serverStatus: ServerStatus): ReactNode {
		let barcodeEnabled: boolean = FunctionHelper.applyValue(
			applicationConfig.webservices.barcode?.enabled, BarcodeWebservice.getEnabledDefault()
		);
		let barcodeStatus: WebserviceStatus = FunctionHelper.applyValue(
			serverStatus.webservices?.barcode?.status, ExecutableStatus.getStatusDefault()
		).toLowerCase() as WebserviceStatus;

		return (
			<AccordionTab key={"barcode"} className={barcodeStatus === WebserviceStatus.ERROR ? "errorneous" : ""}
						  headerTemplate={
							  <>
								  <Translate {...this.props} data="adminscreen.panels.webservices.barcode"
											 namespace="screen"/>
								  {this.renderStatus(serverStatus.webservices?.barcode)}
							  </>
						  }>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid">
						<label className="p-col-12 p-sm-5" htmlFor="control-barcode-enabled">
							<Translate {...this.props} data="adminscreen.panels.webservices.enabled"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={BarcodeWebservice.getEnabledDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-barcode-active" checked={barcodeEnabled}
										 onChange={(e) => this.onChange(
											 WebserviceBarcodeFields.ENABLED, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={!barcodeEnabled}>
						<label className="p-col-12 p-sm-5" htmlFor="control-barcode-instances">
							<Translate {...this.props} data="adminscreen.panels.webservices.instances"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={BarcodeWebservice.getInstancesDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-barcode-instances" step={1} showButtons={true}
										 useGrouping={false}
										 value={FunctionHelper.applyValue(
											 applicationConfig.webservices.barcode?.instances,
											 BarcodeWebservice.getInstancesDefault()
										 )}
										 min={BarcodeWebservice.getInstancesMin()}
										 onChange={(e) => this.onChange(
											 WebserviceBarcodeFields.INSTANCES, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={!barcodeEnabled}>
						<label className="p-col-12 p-sm-5" htmlFor="control-barcode-queue">
							<Translate {...this.props} data="adminscreen.panels.webservices.queue_size"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={BarcodeWebservice.getQueueSizeDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-barcode-queue" min={0} step={1} showButtons={true}
										 useGrouping={false}
										 value={FunctionHelper.applyValue(
											 applicationConfig.webservices.barcode?.queueSize,
											 BarcodeWebservice.getQueueSizeDefault()
										 )}
										 onChange={(e) => this.onChange(
											 WebserviceBarcodeFields.QUEUE, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={!barcodeEnabled}>
						<label className="p-col-12 p-sm-5" htmlFor="control-barcode-maxlatency">
							<Translate {...this.props} data="adminscreen.panels.webservices.maxlatency"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={BarcodeWebservice.getMaxLatencyDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-barcode-maxlatency" step={1} showButtons={true}
										 useGrouping={false}
										 value={FunctionHelper.applyValue(
											 applicationConfig.webservices.barcode?.maxLatency,
											 BarcodeWebservice.getMaxLatencyDefault()
										 )}
										 min={BarcodeWebservice.getMaxLatencyMin()} suffix={" " + TimeUnit.SECOND}
										 onChange={(e) => this.onChange(
											 WebserviceBarcodeFields.LATENCY, e.value
										 )}/>
						</div>
					</div>
				</fieldset>
			</AccordionTab>
		);
	}

	private renderToolboxTab(applicationConfig: Application, serverStatus: ServerStatus): ReactNode {
		let toolboxEnabled: boolean = FunctionHelper.applyValue(
			applicationConfig.webservices.toolbox?.enabled, ToolboxWebservice.getEnabledDefault()
		);
		let toolboxStatus: WebserviceStatus = FunctionHelper.applyValue(
			serverStatus.webservices?.toolbox?.status, ExecutableStatus.getStatusDefault()
		).toLowerCase() as WebserviceStatus;

		return (
			<AccordionTab key={"toolbox"} className={toolboxStatus === WebserviceStatus.ERROR ? "errorneous" : ""}
						  headerTemplate={
							  <>
								  <Translate {...this.props} data="adminscreen.panels.webservices.toolbox"
											 namespace="screen"/>
								  {this.renderStatus(serverStatus.webservices?.toolbox)}
							  </>
						  }>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid">
						<label className="p-col-12 p-sm-5" htmlFor="control-toolbox-enabled">
							<Translate {...this.props} data="adminscreen.panels.webservices.enabled"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={ToolboxWebservice.getEnabledDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-toolbox-active" checked={toolboxEnabled}
										 onChange={(e) => this.onChange(
											 WebserviceToolboxFields.ENABLED, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={!toolboxEnabled}>
						<label className="p-col-12 p-sm-5" htmlFor="control-toolbox-instances">
							<Translate {...this.props} data="adminscreen.panels.webservices.instances"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={ToolboxWebservice.getInstancesDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-toolbox-instances" step={1} showButtons={true}
										 useGrouping={false}
										 value={FunctionHelper.applyValue(
											 applicationConfig.webservices.toolbox?.instances,
											 ToolboxWebservice.getInstancesDefault()
										 )}
										 min={ToolboxWebservice.getInstancesMin()}
										 onChange={(e) => this.onChange(
											 WebserviceToolboxFields.INSTANCES, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={!toolboxEnabled}>
						<label className="p-col-12 p-sm-5" htmlFor="control-toolbox-queue">
							<Translate {...this.props} data="adminscreen.panels.webservices.queue_size"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={ToolboxWebservice.getQueueSizeDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-toolbox-queue" min={0} step={1} showButtons={true}
										 useGrouping={false}
										 value={FunctionHelper.applyValue(
											 applicationConfig.webservices.toolbox?.queueSize,
											 ToolboxWebservice.getQueueSizeDefault()
										 )}
										 onChange={(e) => this.onChange(
											 WebserviceToolboxFields.QUEUE, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={!toolboxEnabled}>
						<label className="p-col-12 p-sm-5" htmlFor="control-toolbox-maxlatency">
							<Translate {...this.props} data="adminscreen.panels.webservices.maxlatency"
									   namespace="screen"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={ToolboxWebservice.getMaxLatencyDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-toolbox-maxlatency" step={1} showButtons={true}
										 suffix={" " + TimeUnit.SECOND} useGrouping={false}
										 value={FunctionHelper.applyValue(
											 applicationConfig.webservices.toolbox?.maxLatency,
											 ToolboxWebservice.getMaxLatencyDefault()
										 )}
										 min={ToolboxWebservice.getMaxLatencyMin()}
										 onChange={(e) => this.onChange(
											 WebserviceToolboxFields.LATENCY, e.value
										 )}/>
						</div>
					</div>
				</fieldset>
			</AccordionTab>
		);
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		let serverStatus: ServerStatus = ServerStatus.fromJson(this.props.serverStatus?.get());
		let applicationConfig: Application = Application.fromJson(this.state.parameter?.get());

		if (!FunctionHelper.exists(serverStatus) || !FunctionHelper.exists(applicationConfig)) {
			return null;
		}

		let testExceptions: Map<TestCase, Exception> = this.state.testException;

		return (
			<div className="screen admin-webservices-panel" data-help="global.screen.admin.content" data-position="top">
				<div className="toolbar" hidden={!this.state.hasChanges}>
					<p>
						<Translate {...this.props} data="adminscreen.panels.webservices.changes" namespace="screen"/>
					</p>
					<div className="buttons">
						<MenuButton {...this.props} label={Translate.value("buttons.save")}
									disabled={!this.state.hasChanges || testExceptions.size > 0 || this.state.submitted}
									icon={"icon" + (this.state.submitted ? " icon-spinner spin" : " icon-check")}
									className="primary-button" onClick={() => this.submit()}/>
						<MenuButton {...this.props} label={Translate.value("buttons.reset")}
									disabled={!this.state.hasChanges || this.state.submitted}
									icon={"icon icon-rotate-270 small"}
									className="dialog-button" onClick={() => this.reset()}/>
					</div>
				</div>
				<div className="body">
					<h1>
						<Translate {...this.props} data="adminscreen.panels.webservices.title" namespace="screen"/>
						{this.renderHelpButton()}
					</h1>
					<Helper {...this.props}>
						<Translate {...this.props} data={"global.screen.admin.webservices.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					<Helper {...this.props} type={MessageType.WARNING}>
						<Translate {...this.props} data={"global.screen.admin.webservices.info"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					<fieldset className="p-col-12 p-grid">
						{this.renderError()}
					</fieldset>
					<div className="p-grid">
						<fieldset className="p-col-12 p-grid">
							<div className="p-col-12">
								<Accordion>
									{this.renderConverterTab(applicationConfig, serverStatus)}
									{this.renderUrlConverterTab(applicationConfig, serverStatus)}
									{this.renderSignatureTab(applicationConfig, serverStatus)}
									{this.renderPdfaTab(applicationConfig, serverStatus)}
									{this.renderOcrTab(applicationConfig, serverStatus)}
									{this.renderBarcodeTab(applicationConfig, serverStatus)}
									{this.renderToolboxTab(applicationConfig, serverStatus)}
								</Accordion>
							</div>
						</fieldset>
					</div>
				</div>
			</div>
		);
	}
}

const WebservicesPanel = React.memo(WebservicesPanelDefinition,
	(prevProps: Readonly<WebservicesPanelProperties>, nextProps: Readonly<WebservicesPanelProperties>) => {
		if (nextProps.contentMode !== prevProps.contentMode) {
			return false;
		}

		if (prevProps.language !== nextProps.language) {
			return false;
		}

		if (!prevProps.serverStatus?.equals(nextProps.serverStatus)) {
			return false;
		}

		if (!prevProps.applicationConfig?.equals(nextProps.applicationConfig)) {
			return false;
		}

		if (prevProps.showHelp !== nextProps.showHelp) {
			return false;
		}

		return true;
	}
);

export default WebservicesPanel;